import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles } from '../data/articles';
import { ArticleBody } from '../components/article/ArticleBody';
import { ArticleCTABand } from '../components/article/ArticleCTABand';
import { ArticleFAQ } from '../components/article/ArticleFAQ';
import { ArticleTOC } from '../components/article/ArticleTOC';
import { usePageMeta } from '../hooks/usePageMeta';
import { buildMatchLink } from '../utils/matchParams';
const FAQ_ID = 'faqs';
export function ArticlePage() {
  const {
    articleName
  } = useParams<{
    articleName: string;
  }>();
  const location = useLocation();
  // Each article also has an explicit route so it appears as its own URL, and
  // those routes carry no `:articleName` param — fall back to the path.
  const slug = articleName || location.pathname.split('/').filter(Boolean).pop() || '';
  const article = getArticleBySlug(slug);
  usePageMeta(article?.metaTitle || 'Article | FindOnlineColleges', article?.metaDescription || 'Compare accredited online degree programs. Get matched by career goals, schedule, and budget.', {
    ogType: 'article'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <main className="min-h-[60vh] flex items-center justify-center px-4 py-20" data-id="element-1044" data-mp="2htxmw4o7f95l">
        <div className="text-center max-w-md" data-id="element-1045" data-mp="1u6zdfqyi51h6">
          <h1 className="text-2xl font-bold text-navy mb-3" data-id="element-1046" data-mp="1p7mlwswor689">
            Article Not Found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-1047" data-mp="22zb15528eatg">
            We couldn't find that article. Browse our guides or get matched with
            accredited programs instead.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white font-bold py-3 px-7 rounded-lg text-sm transition-colors duration-200" data-id="element-1048" data-mp="1aigqzh4vs266">
            Get Matched
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1049" data-mp="35l3owynq4uhw" />
          </Link>
        </div>
      </main>;
  }
  const related = getRelatedArticles(article.slug);
  const midpoint = Math.ceil(article.sections.length / 2);
  const matchHref = buildMatchLink('/request-information', article.match);
  return <main className="bg-white" data-id="element-1050" data-mp="3k9rfeqipqe0z">
      {/* Hero */}
      <header className="hero-bg" data-id="element-1051" data-mp="2mnkdjodsmzsb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1052" data-mp="2mobz06dofh4v">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1053" data-mp="2rmx1ydkbcex0">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-1054" data-mp="2wkykmxdtuc7y">
              <li data-id="element-1055" data-mp="2wnb79nuj5arc">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1056" data-mp="31jryws0xu796">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1057" data-mp="bty3kozbtqq9">/</li>
              <li className="text-white/90" data-id="element-1058" data-mp="gtss4lvmq485">{article.category}</li>
            </ol>
          </nav>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-1059" data-mp="21bv85eat03bu">
            {article.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-4xl" data-id="element-1060" data-mp="1wcid469s3py6">
            {article.title}
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl mb-6" data-id="element-1061" data-mp="1rjp599j0k1hv">
            {article.intro}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/60" data-id="element-1062" data-mp="1hnm70era8n07">
            <span className="inline-flex items-center gap-1.5" data-id="element-1063" data-mp="1dmip7mpocgwc">
              <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1064" data-mp="1dkz8xw40q5uk" />
              Updated {article.updated}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-1065" data-mp="13o30tri17lqj">
              <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1066" data-mp="13pxhuuq6mtpc" />
              {article.readMinutes} min read
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1067" data-mp="2y2x59a0t0s76">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-1068" data-mp="1vq0xu8xhuaes">
          <article className="lg:w-2/3 space-y-12" data-id="element-1069" data-mp="1zq1c9tklellu">
            <ArticleBody sections={article.sections} interruptAfter={midpoint} interruption={<ArticleCTABand variant="inline" body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-1071" data-mp="1qpmngk8f6odl" />} data-id="element-1070" data-mp="1zprma5zjseg5" />
            <ArticleFAQ id={FAQ_ID} faqs={article.faqs} data-id="element-1072" data-mp="1gs8jvl7lhu2p" />
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/3 space-y-8" data-id="element-1073" data-mp="131lkdqu0g05k">
            <div className="lg:sticky lg:top-24 space-y-8" data-id="element-1074" data-mp="17yvirvzdnm5j">
              <div className="bg-navy rounded-xl p-6" data-id="element-1075" data-mp="17zdaqgf0be7n">
                <h2 className="text-lg font-bold text-white mb-2" data-id="element-1076" data-mp="t1kx5tt0szw3">
                  Get Matched
                </h2>
                <p className="text-sm text-white/75 leading-relaxed mb-5" data-id="element-1077" data-mp="xzmgmfucz183">
                  Answer a few questions and we'll connect you with accredited
                  programs that fit your goals, schedule, and budget.
                </p>
                <Link to={matchHref} className="block text-center bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm transition-colors duration-200" data-id="element-1078" data-mp="afwzqrbgfq7r">
                  {article.cta.buttonLabel}
                </Link>
                <p className="mt-3 text-xs text-white/55 text-center" data-id="element-1079" data-mp="xbng5z6hkxsp">
                  No cost to you. No obligation.
                </p>
              </div>

              <ArticleTOC sections={article.sections} faqId={FAQ_ID} data-id="element-1080" data-mp="172i3l6vgy3fq" />

              <div className="border-t border-gray-200 pt-6" data-id="element-1081" data-mp="12488sxdnp4un">
                <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-4" data-id="element-1082" data-mp="126t9hj27xeqt">
                  Keep Reading
                </h2>
                <ul className="space-y-4" data-id="element-1083" data-mp="1c3z3az27vjuq">
                  {article.internalLinks.map(link => <li key={link.anchor} data-id="element-1084" data-mp="1j75kjkhn3u1i">
                      <Link to={buildMatchLink(link.to, link.match ?? article.match)} className="group flex items-start gap-2 text-sm text-gray-700 hover:text-navy transition-colors duration-150" data-id="element-1085" data-mp="1j49mlw5sl5d2">
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-150" aria-hidden="true" data-id="element-1086" data-mp="22t1x3o0we1r4" />
                        <span className="capitalize" data-id="element-1087" data-mp="2bsoxq7lsz3j4">{link.anchor}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Related articles */}
      {related.length > 0 && <section aria-labelledby="related-heading" className="bg-light border-t border-gray-200 py-14" data-id="element-1088" data-mp="mhohlda1km3i">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1089" data-mp="a52p1pfml8t3">
            <h2 id="related-heading" className="text-2xl font-bold text-navy mb-6" data-id="element-1090" data-mp="a3rl9pm6k11z">
              Related Guides
            </h2>
            <ul className="grid sm:grid-cols-3 gap-5" data-id="element-1091" data-mp="99ci61wpfbb">
              {related.map(item => <li key={item.slug} className="flex" data-id="element-1092" data-mp="xuomo8gcnfyz">
                  <Link to={`/article/${item.slug}`} className="group bg-white border border-gray-200 rounded-xl p-6 flex flex-col w-full hover:border-navy transition-colors duration-200" data-id="element-1093" data-mp="svkd3gpijg84">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-1094" data-mp="ixo3wug4yvcn">
                      {item.category}
                    </span>
                    <span className="font-bold text-gray-900 leading-snug mb-3" data-id="element-1095" data-mp="33n3rzhnz05yl">
                      {item.title}
                    </span>
                    <span className="mt-auto inline-flex items-center text-sm font-semibold text-navy" data-id="element-1096" data-mp="3ten7xhv7plh6">
                      Read guide
                      <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" data-id="element-1097" data-mp="3kcdykvbojy5d" />
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}

      <ArticleCTABand body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-1098" data-mp="kybmli05kngl" />
    </main>;
}